import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Card, CardContent } from './ui/card';
import { Star, User as UserIcon } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';

const ReviewsListModal = ({ open, onOpenChange, userId, userName }) => {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [avgRating, setAvgRating] = useState(0);

  useEffect(() => {
    if (open && userId) {
      fetchReviews();
    }
  }, [open, userId]);

  const fetchReviews = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('reviews')
        .select('*, from_user:profiles!from_user_id(name, business_name, role, town)')
        .eq('to_user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setReviews(data || []);

      if (data && data.length > 0) {
        const avg = data.reduce((sum, r) => sum + r.rating, 0) / data.length;
        setAvgRating(avg);
      } else {
        setAvgRating(0);
      }
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div
        className="flex gap-1"
        role="img"
        aria-label={t('services.ratingOutOf', { rating, defaultValue: '{{rating}} out of 5 stars' })}
      >
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            aria-hidden="true"
            className={star <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {t('reviews.reviewsFor')} {userName || 'User'}
          </DialogTitle>
          {reviews.length > 0 && (
            <div className="flex items-center gap-3 pt-2">
              <div className="flex">
                {renderStars(Math.round(avgRating))}
              </div>
              <span className="font-semibold text-lg">{avgRating.toFixed(1)}</span>
              <span className="text-muted-foreground">{t('services.reviewCount', { count: reviews.length })}</span>
            </div>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" role="status" aria-label={t('common.loading', 'Loading...')} />
          </div>
        ) : reviews.length === 0 ? (
          <div className="py-12 text-center">
            <UserIcon className="mx-auto mb-4 text-muted-foreground" size={48} />
            <h3 className="font-heading text-xl font-semibold mb-2">{t('reviews.noReviews')}</h3>
            <p className="text-muted-foreground">{t('reviews.beFirst')}</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[50vh] pr-4">
            <div className="space-y-4">
              {reviews.map((review) => (
                <Card key={review.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarFallback>{(review.from_user?.role === 'employer' || review.from_user?.role === 'owner' ? (review.from_user?.business_name || 'Employer') : (review.from_user?.name || 'Anonymous'))?.[0] || 'U'}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-semibold">{review.from_user?.role === 'employer' || review.from_user?.role === 'owner' ? (review.from_user?.business_name || 'Employer') : (review.from_user?.name || 'Anonymous')}</h4>
                            <p className="text-xs text-muted-foreground">{review.from_user?.town}</p>
                          </div>
                          <div className="text-right">
                            {renderStars(review.rating)}
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(review.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        {review.comment && (
                          <p className="text-sm text-foreground mt-2">{review.comment}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReviewsListModal;
